import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Save, User, Calendar, FileText,
  ChevronDown, Search, Check, X, Package
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function MobileInvoiceCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);
  const [showArticlePicker, setShowArticlePicker] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [articleSearch, setArticleSearch] = useState('');

  const [invoice, setInvoice] = useState({
    customer_id: '',
    customer_name: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    our_reference: '',
    your_reference: '',
    lines: [{ description: '', quantity: 1, unit: 'st', unit_price: 0, vat_rate: 25, article_id: '' }],
    notes: ''
  });

  // Fetch customers and articles
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const headers = { 'Authorization': `Bearer ${token}` };
        const [custRes, artRes] = await Promise.all([
          fetch(`${API_BASE}/api/customers`, { headers }),
          fetch(`${API_BASE}/api/articles`, { headers })
        ]);
        
        const custData = await custRes.json();
        const artData = await artRes.json();
        
        setCustomers(Array.isArray(custData) ? custData : []);
        setArticles(Array.isArray(artData) ? artData : []);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const selectCustomer = (customer) => {
    setInvoice(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name
    }));
    setShowCustomerPicker(false);
    setCustomerSearch('');
  };

  const selectArticle = (lineIndex, article) => {
    const newLines = [...invoice.lines];
    newLines[lineIndex] = {
      ...newLines[lineIndex],
      article_id: article.id,
      description: article.name,
      unit_price: article.price || 0,
      unit: article.unit || 'st',
      vat_rate: article.vat_rate || 25
    };
    setInvoice(prev => ({ ...prev, lines: newLines }));
    setShowArticlePicker(null);
    setArticleSearch('');
  };

  const updateLine = (index, field, value) => {
    const newLines = [...invoice.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setInvoice(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
    setInvoice(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit: 'st', unit_price: 0, vat_rate: 25, article_id: '' }]
    }));
  };

  const removeLine = (index) => {
    if (invoice.lines.length > 1) {
      setInvoice(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let vatAmount = 0;
    
    invoice.lines.forEach(line => {
      const lineTotal = (line.quantity || 0) * (line.unit_price || 0);
      subtotal += lineTotal;
      vatAmount += lineTotal * ((line.vat_rate || 0) / 100);
    });
    
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      total: Math.round((subtotal + vatAmount) * 100) / 100
    };
  };

  const handleSave = async (asDraft = true) => {
    if (!invoice.customer_id) {
      alert('Välj en kund först');
      return;
    }
    
    if (invoice.lines.every(l => !l.description && l.unit_price === 0)) {
      alert('Lägg till minst en rad');
      return;
    }

    setSaving(true);
    try {
      const totals = calculateTotals();
      const payload = {
        ...invoice,
        subtotal: totals.subtotal,
        vat_amount: totals.vatAmount,
        total: totals.total,
        status: asDraft ? 'draft' : 'sent',
        lines: invoice.lines.map(line => ({
          ...line,
          total: (line.quantity || 0) * (line.unit_price || 0)
        }))
      };

      const response = await fetch(`${API_BASE}/api/invoices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        navigate('/m/invoices');
      } else {
        const error = await response.json();
        alert(error.detail || 'Kunde inte spara faktura');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Fel vid sparande');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();
  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase())
  );
  const filteredArticles = articles.filter(a => 
    a.name?.toLowerCase().includes(articleSearch.toLowerCase())
  );

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m/invoices')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('invoices.newInvoice')}</div>
        </div>
        <button 
          className="mobile-header-btn" 
          onClick={() => handleSave(true)}
          disabled={saving}
          style={{ background: 'rgba(255,255,255,0.25)' }}
        >
          <Save size={20} />
        </button>
      </header>

      {/* Content */}
      <main className="mobile-content" style={{ paddingBottom: '180px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : (
          <>
            {/* Customer Selection */}
            <div className="mobile-card">
              <label className="mobile-label">{t('invoices.customer')} *</label>
              <button
                onClick={() => setShowCustomerPicker(true)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  fontSize: '15px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <User size={18} color="#64748b" />
                  <span style={{ color: invoice.customer_name ? '#1e293b' : '#94a3b8' }}>
                    {invoice.customer_name || 'Välj kund...'}
                  </span>
                </div>
                <ChevronDown size={18} color="#64748b" />
              </button>
            </div>

            {/* Dates */}
            <div className="mobile-card">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label className="mobile-label">{t('invoices.invoiceDate')}</label>
                  <input
                    type="date"
                    className="mobile-input"
                    value={invoice.invoice_date}
                    onChange={(e) => setInvoice(prev => ({ ...prev, invoice_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mobile-label">{t('invoices.dueDate')}</label>
                  <input
                    type="date"
                    className="mobile-input"
                    value={invoice.due_date}
                    onChange={(e) => setInvoice(prev => ({ ...prev, due_date: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Invoice Lines */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">{t('invoices.lines')}</h3>
              <button className="mobile-section-action" onClick={addLine}>
                <Plus size={16} /> {t('invoices.addLine')}
              </button>
            </div>

            {invoice.lines.map((line, idx) => (
              <div key={idx} className="mobile-card" style={{ position: 'relative' }}>
                {invoice.lines.length > 1 && (
                  <button
                    onClick={() => removeLine(idx)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} color="#dc2626" />
                  </button>
                )}
                
                {/* Article Selection */}
                <div className="mobile-input-group">
                  <label className="mobile-label">{t('invoices.article')}</label>
                  <button
                    onClick={() => setShowArticlePicker(idx)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      background: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Package size={16} color="#64748b" />
                      <span style={{ color: line.description ? '#1e293b' : '#94a3b8' }}>
                        {line.description || 'Välj artikel eller skriv...'}
                      </span>
                    </div>
                    <ChevronDown size={16} color="#64748b" />
                  </button>
                </div>

                {/* Description */}
                <div className="mobile-input-group">
                  <label className="mobile-label">{t('common.description')}</label>
                  <input
                    type="text"
                    className="mobile-input"
                    placeholder="Beskrivning..."
                    value={line.description}
                    onChange={(e) => updateLine(idx, 'description', e.target.value)}
                  />
                </div>

                {/* Quantity, Unit, Price */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: '8px' }}>
                  <div>
                    <label className="mobile-label">{t('invoices.quantity')}</label>
                    <input
                      type="number"
                      className="mobile-input"
                      value={line.quantity}
                      onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="1"
                    />
                  </div>
                  <div>
                    <label className="mobile-label">{t('invoices.unit')}</label>
                    <select
                      className="mobile-input"
                      value={line.unit}
                      onChange={(e) => updateLine(idx, 'unit', e.target.value)}
                      style={{ padding: '12px' }}
                    >
                      <option value="st">st</option>
                      <option value="tim">tim</option>
                      <option value="kg">kg</option>
                      <option value="m">m</option>
                    </select>
                  </div>
                  <div>
                    <label className="mobile-label">{t('invoices.unitPrice')}</label>
                    <input
                      type="number"
                      className="mobile-input"
                      value={line.unit_price}
                      onChange={(e) => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* VAT */}
                <div style={{ marginTop: '8px' }}>
                  <label className="mobile-label">{t('invoices.vatRate')}</label>
                  <select
                    className="mobile-input"
                    value={line.vat_rate}
                    onChange={(e) => updateLine(idx, 'vat_rate', parseInt(e.target.value))}
                    style={{ padding: '12px' }}
                  >
                    <option value="25">25%</option>
                    <option value="12">12%</option>
                    <option value="6">6%</option>
                    <option value="0">0%</option>
                  </select>
                </div>

                {/* Line Total */}
                <div style={{ 
                  marginTop: '12px', 
                  paddingTop: '12px', 
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#64748b', fontSize: '13px' }}>{t('invoices.lineTotal')}</span>
                  <span style={{ fontWeight: 600 }}>
                    {((line.quantity || 0) * (line.unit_price || 0)).toLocaleString('sv-SE')} kr
                  </span>
                </div>
              </div>
            ))}

            {/* Notes */}
            <div className="mobile-card">
              <label className="mobile-label">{t('invoices.notes')}</label>
              <textarea
                className="mobile-input"
                rows={3}
                placeholder="Valfria anteckningar..."
                value={invoice.notes}
                onChange={(e) => setInvoice(prev => ({ ...prev, notes: e.target.value }))}
                style={{ resize: 'none' }}
              />
            </div>
          </>
        )}
      </main>

      {/* Totals & Actions - Fixed at bottom */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #e2e8f0',
        padding: '16px',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        zIndex: 100
      }}>
        {/* Totals */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ color: '#64748b' }}>{t('invoices.subtotal')}</span>
            <span>{totals.subtotal.toLocaleString('sv-SE')} kr</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ color: '#64748b' }}>{t('invoices.vat')}</span>
            <span>{totals.vatAmount.toLocaleString('sv-SE')} kr</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '18px' }}>
            <span>{t('common.total')}</span>
            <span style={{ color: '#1e3a5f' }}>{totals.total.toLocaleString('sv-SE')} kr</span>
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="mobile-btn mobile-btn-outline"
            onClick={() => handleSave(true)}
            disabled={saving}
            style={{ flex: 1 }}
          >
            {saving ? <div className="mobile-spinner" style={{ width: '18px', height: '18px' }} /> : 'Spara utkast'}
          </button>
          <button 
            className="mobile-btn mobile-btn-primary"
            onClick={() => handleSave(false)}
            disabled={saving}
            style={{ flex: 1 }}
          >
            {saving ? <div className="mobile-spinner" style={{ width: '18px', height: '18px', borderTopColor: 'white' }} /> : 'Skapa & Skicka'}
          </button>
        </div>
      </div>

      {/* Customer Picker Sheet */}
      <div className={`mobile-overlay ${showCustomerPicker ? 'visible' : ''}`} onClick={() => setShowCustomerPicker(false)} />
      <div className={`mobile-sheet ${showCustomerPicker ? 'open' : ''}`}>
        <div className="mobile-sheet-handle" />
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Välj kund</h3>
        
        <div className="mobile-search" style={{ marginBottom: '16px' }}>
          <Search size={18} className="mobile-search-icon" />
          <input
            type="text"
            className="mobile-search-input"
            placeholder="Sök kunder..."
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
          />
        </div>
        
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {filteredCustomers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
              Inga kunder hittades
            </div>
          ) : (
            filteredCustomers.map(customer => (
              <div
                key={customer.id}
                onClick={() => selectCustomer(customer)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '14px',
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#e0f2fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <User size={20} color="#0284c7" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{customer.name}</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>{customer.org_number}</div>
                </div>
                {invoice.customer_id === customer.id && (
                  <Check size={20} color="#16a34a" />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Article Picker Sheet */}
      <div className={`mobile-overlay ${showArticlePicker !== null ? 'visible' : ''}`} onClick={() => setShowArticlePicker(null)} />
      <div className={`mobile-sheet ${showArticlePicker !== null ? 'open' : ''}`}>
        <div className="mobile-sheet-handle" />
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Välj artikel</h3>
        
        <div className="mobile-search" style={{ marginBottom: '16px' }}>
          <Search size={18} className="mobile-search-icon" />
          <input
            type="text"
            className="mobile-search-input"
            placeholder="Sök artiklar..."
            value={articleSearch}
            onChange={(e) => setArticleSearch(e.target.value)}
          />
        </div>
        
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {filteredArticles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
              Inga artiklar hittades
            </div>
          ) : (
            filteredArticles.map(article => (
              <div
                key={article.id}
                onClick={() => selectArticle(showArticlePicker, article)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '14px',
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px'
                }}>
                  <Package size={20} color="#9333ea" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{article.name}</div>
                  <div style={{ fontSize: '13px', color: '#64748b' }}>{article.article_number}</div>
                </div>
                <div style={{ fontWeight: 600, color: '#1e3a5f' }}>
                  {(article.price || 0).toLocaleString('sv-SE')} kr
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
